import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { faGear as falGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { faHouse as falHouse } from '@fortawesome/pro-light-svg-icons/faHouse';
import { faMagnifyingGlassChart as falMagnifyingGlassChart } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlassChart';
import { faPeopleRoof as falPeopleRoof } from '@fortawesome/pro-light-svg-icons/faPeopleRoof';
import { faUserGraduate as falUserGraduate } from '@fortawesome/pro-light-svg-icons/faUserGraduate';
import { faQrcode } from '@fortawesome/pro-regular-svg-icons';
import { faGear as fasGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faHouse as fasHouse } from '@fortawesome/pro-solid-svg-icons/faHouse';
import { faMagnifyingGlassChart as fasMagnifyingGlassChart } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlassChart';
import { faPeopleRoof as fasPeopleRoof } from '@fortawesome/pro-solid-svg-icons/faPeopleRoof';
import { faUserGraduate as fasUserGraduate } from '@fortawesome/pro-solid-svg-icons/faUserGraduate';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Stack } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { CorePermissions, PermissionExplorer } from "app/appPermissions";
import { TFunction } from "i18next";
import { useSession } from "providers/SessionProvider";
import * as React from "react";
import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { DashboardPaths } from 'routes/DashboardRouter';
import { FoundationPaths } from "routes/FoundationRouter";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { LmsPaths } from 'routes/LmsRouter';
import { QrCodeAccess } from 'views/foundation/user/dialogs/QrCodeAccess';
import { OutsideOptions, useOutsideLayout } from "./OutsideLayoutProvider";

import { faChartUser as falChartUser } from '@fortawesome/pro-light-svg-icons/faChartUser';
import { faChartUser as fasChartUser } from '@fortawesome/pro-solid-svg-icons/faChartUser';

import { faChartSimpleHorizontal as falChartSimpleHorizontal } from '@fortawesome/pro-light-svg-icons/faChartSimpleHorizontal';
import { faChartSimpleHorizontal as fasChartSimpleHorizontal } from '@fortawesome/pro-solid-svg-icons/faChartSimpleHorizontal';
import { AppSettingsResponse } from 'generated/CoreApi_v1';
import { IntegrationPaths } from 'routes/IntegrationRouter';


type MenuOptions = {
    option: OutsideOptions;
    icon: IconDefinition;
    selectedIcon: IconDefinition;
    gap?: number;
    title: string;
};

const buildMenuItems = (pe: PermissionExplorer, config: AppSettingsResponse, t: TFunction): MenuOptions[] => {

    var items: MenuOptions[] = [
        { option: "home", icon: falHouse, selectedIcon: fasHouse, title: t("App.Menu.Sidebar.Home") },
        { option: "lms", icon: falUserGraduate, selectedIcon: fasUserGraduate, title: t("App.Menu.Sidebar.Lms") },
        { option: "knowledge", icon: falPeopleRoof, selectedIcon: fasPeopleRoof, title: t("App.Menu.Sidebar.Knowledge") },
    ];

    if (config.performaIntegrationEnabled && pe.hasOne([CorePermissions.PerformanceReviewAccess])) {
        items.push({ option: "performance", icon: falChartUser, selectedIcon: fasChartUser, title: t("App.Menu.Sidebar.Performance") });
    }

    if (config.performaIntegrationEnabled && pe.hasOne([CorePermissions.PerformanceGoalsAccess])) {
        items.push({ option: "goals", icon: falChartSimpleHorizontal, selectedIcon: fasChartSimpleHorizontal, title: t("App.Menu.Sidebar.Goals") });
    }

    // { option: "feedback", icon: falHandshake, selectedIcon: fasHandshake, title: t("App.Menu.Sidebar.Feedback") },
    // { option: "career", icon: falArrowUpRightDots, selectedIcon: fasArrowUpRightDots, title: t("App.Menu.Sidebar.Career") },

    items.push({ option: "dashboard", icon: falMagnifyingGlassChart, selectedIcon: fasMagnifyingGlassChart, gap: 2, title: t("App.Menu.Sidebar.Dashboard") });

    if (pe.canViewSetup) {
        items.push({ option: "setup", icon: falGear, selectedIcon: fasGear, gap: 2, title: t("App.Menu.Sidebar.Setup") });
    }

    return items;
};

export const SideBarFindCurrentOption = (): OutsideOptions => {
    var pathname = document.location.pathname.toLowerCase();
    if (pathname.indexOf(FoundationPaths.Prefix.toLowerCase()) > -1) return "setup";
    if (pathname.indexOf(LmsPaths.Prefix.toLowerCase()) > -1) return "lms";
    if (pathname.indexOf(KnowledgePaths.Prefix.toLowerCase()) > -1) return "knowledge";
    if (pathname.indexOf(DashboardPaths.Prefix.toLowerCase()) > -1) return "dashboard";
    if (pathname == IntegrationPaths.Goals.toLowerCase()) return "goals";
    if (pathname == IntegrationPaths.Performance.toLowerCase()) return "performance";
    return "home";
};

export const Sidebar: React.FC = () => {
    const session = useSession();
    const { t } = useTranslation("shared");
    const { changeOption, selectedOption } = useOutsideLayout();
    const menuItems = buildMenuItems(session.permissionExplorer, session.config, t);
    const [showQrCode, setShowQrCode] = useState(false);

    return (
        <>
            <Stack direction="column" height="100%" justifyContent="space-between" alignItems="center">
                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                    {
                        menuItems.map((m, index) => {
                            var selected = m.option == selectedOption;
                            return (
                                <Grid key={index}>
                                    <Box sx={{ marginTop: m.gap, color: (theme) => selected ? theme.extended.workArea.sideBar.selectedColor : undefined }}>
                                        <FontAwesomeIcon
                                            title={m.title}
                                            icon={selected ? m.selectedIcon : m.icon}
                                            className={AppGlobalClassKeys.clickable}
                                            onClick={() => changeOption(m.option)}
                                        />
                                    </Box>
                                </Grid>
                            );
                        })
                    }
                </Grid>
                <Box pb={2}>
                    <FontAwesomeIcon
                        title={t("core:Auth.QrCode.Title")}
                        icon={faQrcode}
                        className={AppGlobalClassKeys.clickable}
                        onClick={() => setShowQrCode(true)}
                    />
                </Box>
            </Stack>

            {showQrCode && <QrCodeAccess onClose={() => setShowQrCode(false)} />}
        </>
    );
};
